import { FC, KeyboardEvent, MouseEvent, useCallback } from 'react';
import { twJoin } from 'tailwind-merge';

import {
  RyeconFavoriteFilled,
  RyeconFavoriteOutline,
} from '@/context/AccountNavigation/RyeconFavorite';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import {
  useAddFavoriteAsset,
  useDeleteFavoriteAsset,
} from '@/hooks/admin/favoriteAssets';
import {
  useAddFavoriteProject,
  useDeleteFavoriteProject,
} from '@/hooks/admin/favoriteProjects';

const REUSED_IDENTIFIER = 'favorite-toggle';

// Base reusable component for favorite toggle UI
type FavoriteToggleProps = {
  isFavorite?: boolean;
  iconSize?: 'sm' | 'md' | 'lg';
  label?: string;
  onClick: () => void;
  testId?: string;
};

// Specific component props
type FavoriteProjectToggleProps = {
  isFavorite?: boolean;
  iconSize?: 'sm' | 'md' | 'lg';
  projectId: number;
  label?: string;
};

type FavoriteAssetToggleProps = {
  isFavorite?: boolean;
  iconSize?: 'sm' | 'md' | 'lg';
  assetId: string;
  projectId?: number;
  name?: string;
  platform?: string;
  assetType?: string;
  environmentId?: string;
  label?: string;
};

// Reusable FavoriteToggle component with UI logic
export const FavoriteToggle: FC<FavoriteToggleProps> = ({
  isFavorite = false,
  label,
  onClick,
  testId = REUSED_IDENTIFIER,
}) => {
  const handleClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      onClick();
      // blur on unfavorite so it doesn't stay focused
      isFavorite && e.currentTarget.blur();
    },
    [onClick, isFavorite],
  );

  // buttons fire on enter key down
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  // buttons fire on space key up
  const onKeyUp = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (e.key === ' ') {
        e.preventDefault();
        onClick();
      }
    },
    [onClick],
  );

  const classNames = twJoin(testId, 'relative z-[1]');

  // we are not using a button because we want slightly different behavior for clicks & keyboard events
  // typically this is an anti-pattern; please do not replicate without talking to #dev-guild-frontend
  return (
    <div
      className={classNames}
      role="button"
      tabIndex={0}
      onClick={handleClick}
      onKeyDown={onKeyDown}
      onKeyUp={onKeyUp}
      aria-pressed={isFavorite}
      aria-label="favorite"
      data-testid={testId}
      data-trackingid={testId}
      title={`${isFavorite ? 'remove' : 'add'} favorite`}
    >
      {isFavorite ? <RyeconFavoriteFilled /> : <RyeconFavoriteOutline />}
      <span className="sr-only">{label}</span>
    </div>
  );
};

// FavoriteAssetToggle with asset-specific business logic
export const FavoriteAssetToggle: FC<FavoriteAssetToggleProps> = ({
  isFavorite = false,
  iconSize,
  label,
  assetId,
  projectId,
  name,
  platform,
  assetType,
  environmentId,
}) => {
  const { accountId } = useDbtCloudAccount();
  const addFavoriteAsset = useAddFavoriteAsset();
  const deleteFavoriteAsset = useDeleteFavoriteAsset();

  const handleToggle = useCallback(() => {
    const mutationData = {
      accountId: accountId ?? 0,
      assetId,
      projectId,
      environmentId,
    };

    if (isFavorite) {
      deleteFavoriteAsset.mutate(mutationData);
    } else {
      addFavoriteAsset.mutate({
        ...mutationData,
        name,
        platform,
        assetType,
      });
    }
  }, [
    addFavoriteAsset,
    deleteFavoriteAsset,
    isFavorite,
    assetId,
    projectId,
    environmentId,
    name,
    platform,
    assetType,
    accountId,
  ]);

  return (
    <FavoriteToggle
      isFavorite={isFavorite}
      iconSize={iconSize}
      label={label}
      onClick={handleToggle}
      testId="favorite-asset-toggle"
    />
  );
};

// FavoriteProjectToggle with project-specific business logic
export const FavoriteProjectToggle: FC<FavoriteProjectToggleProps> = ({
  isFavorite = false,
  projectId,
  iconSize,
  label,
}) => {
  const { accountId } = useDbtCloudAccount();
  const addFavoriteProject = useAddFavoriteProject();
  const deleteFavoriteProject = useDeleteFavoriteProject();

  const handleToggle = useCallback(() => {
    const addOrRemove = isFavorite ? deleteFavoriteProject : addFavoriteProject;

    addOrRemove.mutate({
      accountId: accountId ?? 0,
      projectId,
    });
  }, [addFavoriteProject, deleteFavoriteProject, isFavorite, projectId, accountId]);

  return (
    <FavoriteToggle
      isFavorite={isFavorite}
      iconSize={iconSize}
      label={label}
      onClick={handleToggle}
      testId="favorite-project-toggle"
    />
  );
};
